import React, { useState } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { akhdariContent } from '../data/akhdariData';
import './AkhdariPage.css';

const BOOKMARKS_KEY = 'akhdari_bookmarks';
const FONT_SIZE_KEY = 'book_font_size';

const getBookmarks = (): number[] => {
  const stored = localStorage.getItem(BOOKMARKS_KEY);
  return stored ? JSON.parse(stored) : [];
};

const saveBookmarks = (bookmarks: number[]) => {
  localStorage.setItem(BOOKMARKS_KEY, JSON.stringify(bookmarks));
};

const getFontOffset = (): number => {
  return parseInt(localStorage.getItem(FONT_SIZE_KEY) || '0', 10);
};

export const AkhdariPage: React.FC = () => {
  const { colors } = useTheme();
  const [selectedChapter, setSelectedChapter] = useState<number | null>(null);
  const [bookmarks, setBookmarks] = useState<number[]>(getBookmarks);
  const [showBookmarksOnly, setShowBookmarksOnly] = useState(false);
  const [fontOffset, setFontOffset] = useState(getFontOffset);

  const toggleBookmark = (index: number) => {
    const updated = bookmarks.includes(index)
      ? bookmarks.filter(b => b !== index)
      : [...bookmarks, index];
    saveBookmarks(updated);
    setBookmarks(updated);
  };

  const changeFontSize = (delta: number) => {
    const next = Math.min(Math.max(fontOffset + delta, -4), 8);
    localStorage.setItem(FONT_SIZE_KEY, next.toString());
    setFontOffset(next);
  };

  const chapters = akhdariContent
    .map((item, index) => ({ ...item, index }))
    .filter(item => !showBookmarksOnly || bookmarks.includes(item.index));

  if (selectedChapter !== null) {
    const section = akhdariContent[selectedChapter];
    const total = akhdariContent.length;
    const isBookmarked = bookmarks.includes(selectedChapter);

    return (
      <div className="akhdari-page" style={{ backgroundColor: colors.background, color: colors.text }}>
        <div className="chapter-header">
          <button className="back-btn" onClick={() => setSelectedChapter(null)} style={{ color: colors.tint }}>
            ← Chapters
          </button>
          <div className="header-actions">
            <button className="font-btn" onClick={() => changeFontSize(-2)} title="Decrease font">A-</button>
            <button className="font-btn" onClick={() => changeFontSize(2)} title="Increase font">A+</button>
            <button
              className={`bookmark-btn ${isBookmarked ? 'active' : ''}`}
              onClick={() => toggleBookmark(selectedChapter)}
              title={isBookmarked ? 'Remove bookmark' : 'Add bookmark'}
            >
              {isBookmarked ? '🔖' : '🏷️'}
            </button>
          </div>
        </div>
        <h3 style={{ color: colors.tint }}>{section.title}</h3>
        {section.paragraphs.map((p, j) => (
          <div key={j} className="akhdari-row">
            <div className="akhdari-en" style={{ fontSize: `${0.95 + fontOffset * 0.05}rem` }}>{p.en}</div>
            <div className="akhdari-ar" style={{ borderColor: colors.border, fontSize: `${1.1 + fontOffset * 0.05}rem` }}>{p.ar}</div>
          </div>
        ))}
        <div className="chapter-nav" style={{ borderColor: colors.border }}>
          <button
            disabled={selectedChapter === 0}
            onClick={() => setSelectedChapter(selectedChapter - 1)}
            style={{ color: colors.tint }}
          >
            ← Previous
          </button>
          <span style={{ color: colors.muted }}>{selectedChapter + 1} / {total}</span>
          <button
            disabled={selectedChapter === total - 1}
            onClick={() => setSelectedChapter(selectedChapter + 1)}
            style={{ color: colors.tint }}
          >
            Next →
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="akhdari-page" style={{ backgroundColor: colors.background, color: colors.text }}>
      <h2>📕 Mukhtaṣar al-Akhḍarī</h2>
      <p style={{ color: colors.muted }}>Fiqh of worship — Mālikī School</p>
      {bookmarks.length > 0 && (
        <button
          className={`filter-btn ${showBookmarksOnly ? 'active' : ''}`}
          onClick={() => setShowBookmarksOnly(!showBookmarksOnly)}
        >
          🔖 Bookmarks ({bookmarks.length})
        </button>
      )}
      <div className="chapter-list">
        {chapters.map(item => (
          <div
            key={item.index}
            className="chapter-item"
            style={{ backgroundColor: colors.cardBackground, borderColor: colors.border }}
            onClick={() => setSelectedChapter(item.index)}
          >
            <span className="chapter-number" style={{ color: colors.tint }}>{item.index + 1}</span>
            <span className="chapter-title" style={{ color: colors.text }}>{item.title}</span>
            {bookmarks.includes(item.index) && (
              <span
                className="chapter-bookmark"
                onClick={(e) => { e.stopPropagation(); toggleBookmark(item.index); }}
              >
                🔖
              </span>
            )}
            <span className="chapter-arrow" style={{ color: colors.muted }}>›</span>
          </div>
        ))}
      </div>
    </div>
  );
};
